<script setup lang="ts">
import { hasOnlyPlacement } from "~/utils/self-assessment-placement";
import {
  AlternateHintType,
  SelfAssessmentStep,
} from "~/classes/models/self-assessment/self-assessment-step.model";

const props = defineProps({
  step: {
    type: Object as PropType<SelfAssessmentStep>,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  isActive: {
    type: Boolean,
    required: true,
  },
});

const documentId = inject("documentId") as any;
const documentSelfAssessmentStore = useDocumentSelfAssessmentStore(documentId);
const {
  stepExpectedInstances,
  stepRelevantInstances,
  bodyParagraphs,
  stepParagraphPositions,
} = storeToRefs(documentSelfAssessmentStore);

const submittedDocumentStore = useSubmittedDocumentStore(documentId);
const { revision } = storeToRefs(submittedDocumentStore);

const documentChecklistStore = useDocumentChecklistStore(documentId);
const { checklistLevelId, checklistUnitNumber } = storeToRefs(
  documentChecklistStore
);

const setActiveStepIndex = () => {
  documentSelfAssessmentStore.setActiveStepIndex(props.index);
};

const isShaking = ref(false);

watch(
  () => props.step.showInstancesWarning,
  () => {
    if (props.step.showInstancesWarning == true) {
      isShaking.value = true;
      setTimeout(() => {
        isShaking.value = false;
      }, 500); // Duration matches the animation duration
    }
  }
);

const hasErrorMessage = computed(() => {
  return (
    props.step.showInstancesWarning == true ||
    props.step.errorMessage != undefined
  );
});

const studentInstructions = computed(() => {
  let levelId = checklistLevelId.value;
  let unitNumber = checklistUnitNumber.value;

  if (props.step.instructionOverides != undefined) {
    const override = props.step.instructionOverides.find((o) => {
      let matchesLevel = true;

      if (
        checklistLevelId.value != undefined &&
        (o.levelIds ?? []).length > 0
      ) {
        matchesLevel = (o.levelIds ?? []).includes(checklistLevelId.value);
      }

      let matchesUnit = true;

      if (
        checklistUnitNumber.value != undefined &&
        (o.unitNumbers ?? []).length > 0
      ) {
        matchesUnit = (o.unitNumbers ?? []).includes(checklistUnitNumber.value);
      }

      return matchesLevel && matchesUnit;
    });

    if (override != undefined) {
      return override.instructions;
    }
  }

  return props.step.instructions;
});

const showHint = ref(false);

const toggleHint = () => {
  showHint.value = !showHint.value;
};

const stepRef = ref<HTMLElement | null>(null);

onMounted(() => {
  if (props.isActive) {
    scrollToStep();
  }
});

watch(
  () => props.isActive,
  async (newVal) => {
    if (newVal) {
      scrollToStep();
    }
  }
);

const scrollToStep = async () => {
  await nextTick();
  // Wait 500ms
  await new Promise((resolve) => setTimeout(resolve, 100));
  stepRef.value?.scrollIntoView({ behavior: "smooth", block: "nearest" });
};

const animationDuration = 250;

// Transitions
const beforeEnter = (el: Element) => {
  const element = el as HTMLElement;
  element.style.height = "0";
  element.style.opacity = "0";
};

const enter = (el: Element) => {
  const element = el as HTMLElement;
  element.offsetHeight; // trigger reflow

  // Use requestAnimationFrame to ensure child components (like BaseTextEditor) have rendered
  requestAnimationFrame(() => {
    element.style.transition = `height ${animationDuration}ms ease-out, opacity ${animationDuration}ms ease-out`;
    element.style.height = element.scrollHeight + "px";
    element.style.opacity = "1";
  });
};

const afterEnter = (el: Element) => {
  const element = el as HTMLElement;
  element.style.height = "auto";
};

const beforeLeave = (el: Element) => {
  const element = el as HTMLElement;
  element.style.height = element.scrollHeight + "px";
  element.style.opacity = "1";
};

const leave = (el: Element) => {
  const element = el as HTMLElement;
  element.offsetHeight; // trigger reflow

  // Use requestAnimationFrame for consistency with enter
  requestAnimationFrame(() => {
    element.style.transition = `height ${animationDuration}ms ease-in, opacity ${animationDuration}ms ease-in`;
    element.style.height = "0";
    element.style.opacity = "0";
  });
};

const hintToUse = computed(() => {
  if (
    props.step.alternateHint == undefined ||
    props.step.alternateHint.length == 0
  ) {
    return props.step.hint;
  }

  // Check if the step paragraphs match the assignment paragraphs
  if (hasOnlyPlacement(props.step, "topicClinchers")) {
    const stepParagraphs = stepParagraphPositions.value(props.step);
    const altenrateHint = props.step.alternateHint.find(
      (h) => h.type == AlternateHintType.paragraphDifferences
    );

    if (
      stepParagraphs.length != bodyParagraphs.value.length &&
      altenrateHint != undefined
    ) {
      return altenrateHint.hint;
    }
  }

  return props.step.hint;
});
</script>

<template>
  <div
    ref="stepRef"
    class="flex flex-col p-2 rounded hover:bg-light-default cursor-pointer w-full"
    :class="{
      'shake-animation': isShaking,
      'border-b': showHint && studentInstructions != undefined,
    }"
    :style="{
      'scroll-margin-top': '100px',
      'scroll-margin-bottom': '100px',
    }"
    @click="setActiveStepIndex"
  >
    <div class="flex flex-row items-start gap-2 justify-between w-full">
      <div class="flex flex-row items-start gap-2 grow">
        <div class="shrink-0 flex flex-row items-start gap-2">
          <BaseIconLoading v-if="step.isAssessing" />
          <BaseIconCircleCheck
            v-if="step.assessmentResults == true"
            class="mt-[4px] text-success"
          />
          <BaseIconAlert
            v-if="step.assessmentResults == false"
            class="mt-[4px] text-danger"
          />
        </div>
        <span
          class="grow whitespace-normal flex flex-row items-center justify-between"
        >
          {{ step.label }}
          <span
            v-if="
              step.showInstanceCount == true && stepExpectedInstances(step) > 1
            "
            class="font-bold"
          >
            {{ (stepRelevantInstances(step) as any[]).length ?? 0 }}/{{ stepExpectedInstances(step) }}
          </span>
        </span>
      </div>
      <!-- <div v-if="studentInstructions != undefined">
        <BaseIconButton @click.stop.prevent="toggleHint">
          <BaseIconCircleHelp />
        </BaseIconButton>
      </div> -->
    </div>

    <transition
      appear
      @before-enter="beforeEnter"
      @enter="enter"
      @after-enter="afterEnter"
      @before-leave="beforeLeave"
      @leave="leave"
    >
      <div v-if="isActive" class="active-content-wrapper overflow-hidden">
        <div
          v-if="hasErrorMessage != true && step.hint != undefined"
          class="text-body-2 text-secondary flex flex-row pl-2"
        >
          <span class="grow whitespace-normal">
            {{ hintToUse }}
          </span>
        </div>
        <div
          v-if="hasErrorMessage"
          class="text-body-2 text-danger flex flex-row pl-2"
        >
          <span>
            {{
              step.errorMessage ??
              "You need to find " + stepExpectedInstances(step) + " instances."
            }}
          </span>
        </div>
        <div v-if="studentInstructions != undefined" class="p-2">
          <div class="step-instructions border border-medium-tint rounded p-2">
            <BaseTextEditor v-model="studentInstructions" :read-only="true" />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<style>
.shake-animation {
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
    background-color: transparent;
  }
  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-4px);
    @apply bg-danger/10;
  }
  20%,
  40%,
  60%,
  80% {
    transform: translateX(4px);
    @apply bg-danger/10;
  }
}

.step-instructions .ql-editor {
  @apply bg-transparent !important;
  font-size: 10px !important;
}

.step-instructions .read-only .ql-container,
.step-instructions .read-only .ql-editor,
.step-instructions .read-only .editor-container,
.step-instructions .ql-editor p,
.step-instructions .read-only .ql-editor strong,
.step-instructions .ql-editor span {
  font-size: 14px !important;
}
</style>
